import cloneDeep from "lodash/cloneDeep";
import type { Sale } from "./types";

/**
 * Stamping a partner's per-ITEM discounts onto the sale.
 *
 * ⚠️ **MOVED here from `helpers/discountHelper` on 2026-09-08 — one definition, and this is it.**
 * That folder was web-only, and its `index.desktop.ts` now re-exports this file, so web's
 * existing importers are unchanged. The body is verbatim; only its home changed.
 *
 * ⚠️ **One deviation from the verbatim body: `structuredClone` became `lodash/cloneDeep`.**
 * Hermes has none, and shared code that leans on the app's polyfill order fails silently on one
 * platform while every node-run test passes. See `billDiscount.ts` for the full note.
 *
 * It is here rather than there because it is a rule of the PARTNER conversation:
 * the partner's `shouldApplyItemDiscounts` step decides whether to call it, from the
 * partner's profile, and the two belong in the same module. Pure — no store, no platform API.
 */

/** One per-item discount as a partner returns it inside `redemptionInfo.discounts`. */
export interface PartnerItemDiscount {
  discountId: string | number;
  discountName: string;
  value: number;
  type: string;
  couponCode: string;
  isOrderLevel: boolean;
  items: {
    skuCode: string;
    value: number;
    type: string;
    itemId?: string;
  }[];
}

/**
 * A CLONE of the sale in which every bill discount matching a partner coupon carries the
 * `itemIds` that coupon applies to.
 *
 * Matched by `couponCode` against the bill entry's `discountCode` — that pairing is the API's,
 * not a guess: the partner's per-item payload names the campaign by the code the bill records.
 * A bill entry with no match is passed through untouched.
 */
export function applyItemLevelDiscounts(
  saleData: Sale,
  discounts: PartnerItemDiscount[],
): any {
  const clonedSaleData: any = cloneDeep(saleData);

  // couponCode → discount, for a single pass over the bill.
  const couponCodeToDiscount: Record<string, any> = {};
  discounts.forEach((discount: any) => {
    if (discount?.couponCode) {
      couponCodeToDiscount[discount.couponCode] = discount;
    }
  });

  clonedSaleData.billDiscountList = (clonedSaleData.billDiscountList || []).map(
    (billDiscount: any) => {
      const matchedDiscount = couponCodeToDiscount[billDiscount?.discountCode];
      if (!matchedDiscount) return billDiscount;

      const itemIds = (matchedDiscount.items || [])
        .map((discountItem: any) => discountItem?.itemId)
        .filter(Boolean);
      return { ...billDiscount, itemIds };
    },
  );

  return clonedSaleData;
}
